export interface PreinvoiceFormPatch {
    patch: any;       // valores para form.patchValue
    items: any[];     // array plano de UiItem (title/line) para reconstruir FormArray
}

export class PreinvoiceFormAdapter {
    static toFormPatch(params: {
        api: any;
        payMethods: any[];
        taxes: any[];
        concepts: any[];
        contacts: any[];
        bankAccounts: any[];
        cashRegisters: any[];
        partners: any[];
        projects: any[];
        accountingModels: any[];
        masterUnitsMeasurables: any[];
        centers?: any[];
    }): PreinvoiceFormPatch {
        const api = params && params.api ? params.api : {};
        const payMethods = params && params.payMethods ? params.payMethods : [];
        const taxesLookup = params && params.taxes ? params.taxes : [];
        const concepts = params && params.concepts ? params.concepts : [];
        const contacts = params && params.contacts ? params.contacts : [];
        const bankAccounts = params && params.bankAccounts ? params.bankAccounts : [];
        const cashRegisters = params && params.cashRegisters ? params.cashRegisters : [];
        const partners = params && params.partners ? params.partners : [];
        const projects = params && params.projects ? params.projects : [];
        const accountingModels = params && params.accountingModels ? params.accountingModels : [];
        const masterUnitsMeasurables = params && params.masterUnitsMeasurables ? params.masterUnitsMeasurables : [];
        const centers = params && params.centers ? params.centers : [];

        /*
          self.unitMeasurable = (l && l.unitMeasurable) ? l.unitMeasurable : [];
          self.accountingModels = (l && l.accountingModels) ? l.accountingModels : [];
        */

        // header patch
        const patch: any = {
            uid: api.uid || null,
            preInvoiceNumber: api.preInvoiceNumber || '',

            invoiceRelease: api.invoiceRelease ? new Date(api.invoiceRelease) : new Date(),
            invoiceExpiration: api.invoiceExpiration ? new Date(api.invoiceExpiration) : new Date(),

            entranceAccount: api.entranceAccount || null,
            comments: api.comments || '',

            useDeliverAddress: api.useDeliverAddress === true,
            numberKilometers: api.numberKilometers,
            isMeasurable: api.isMeasurable
        };

        const contact = contacts.filter(c => c.id == api.receiverUid)[0];
        if (contact) {
            patch.contactUid = contact;
        }

        const center = centers.filter(c => c.id == api.centerUid)[0];
        if (center) {
            patch.centerUid = center;
        }

        const payMethod = payMethods.filter(c => c.id == api.typePay)[0];
        if (payMethod) {
            patch.payMethodUid = payMethod;
        }

        const bankAccount = bankAccounts.filter(c => c.id == api.typePayBankAccount)[0];
        if (bankAccount) {
            patch.bankAccountUid = bankAccount;
        }

        const cashRegister = cashRegisters.filter(c => c.metadata.id == api.efectivoId)[0];
        if (cashRegister) {
            patch.cashRegisterUid = cashRegister;
        }

        const partner = partners.filter(c => c.id == api.partnersId)[0];
        if (partner) {
            patch.partnerUid = partner;
        }

        const project = projects.filter(c => c.id == api.projectUid)[0];
        if (project) {
            patch.projectUid = project;
        }

        const accountingModel = accountingModels.filter(c => c.id == api.entranceAccount)[0];
        if (accountingModel) {
            patch.entranceAccount = { id: accountingModel.id, display: accountingModel.display };
        }

        const items: any[] = buildItemsFromApi(api, taxesLookup, concepts, masterUnitsMeasurables);

        const flags = calcFlags(items);
        patch.isMeasurable = flags.isMeasurable;
        patch.applyDiscount = flags.applyDiscount;

        return { patch: patch, items: items };
    }
}

/* -------------------- helpers (privados del módulo) -------------------- */

function buildItemsFromApi(api: any, taxesLookup: any[], concepts: any[], masterUnitsMeasurables: any[]): any[] {
    const res: any[] = [];

    const groups = (api.invoiceLineGroups || []).slice();
    groups.sort(function (a: any, b: any) {
        return (Number(a.sortOrder) || 0) - (Number(b.sortOrder) || 0);
    });

    const lines = (api.invoiceLines || []).slice();
    lines.sort(function (a: any, b: any) {
        return (Number(a.position) || 0) - (Number(b.position) || 0);
    });

    // agrupar líneas por groupId
    const linesByGroupId: any = {};
    const ungrouped: any[] = [];

    for (let i = 0; i < lines.length; i++) {
        const ln = lines[i];
        const gid = ln.invoiceLineGroupId;

        if (gid == null) {
            ungrouped.push(ln);
            continue;
        }

        if (!linesByGroupId[gid]) linesByGroupId[gid] = [];
        linesByGroupId[gid].push(ln);
    }

    // grupos (title + lines)
    for (let g = 0; g < groups.length; g++) {
        const gr = groups[g];

        // title
        res.push({
            type: 'title',
            title: gr.title || ''
        });

        // lines del grupo
        const gid2 = gr.id; // viene del backend
        const glines = gid2 != null ? (linesByGroupId[gid2] || []) : [];

        for (let l = 0; l < glines.length; l++) {
            res.push(apiLineToUiItem(glines[l], taxesLookup, concepts, masterUnitsMeasurables));
        }
    }

    // líneas sin grupo
    for (let u = 0; u < ungrouped.length; u++) {
        res.push(apiLineToUiItem(ungrouped[u], taxesLookup, concepts, masterUnitsMeasurables));
    }

    return res;
}

function apiLineToUiItem(line: any, taxesLookup: any[], concepts: any[], masterUnitsMeasurables: any[]): any {
    const masterUnitsMeasurable = masterUnitsMeasurables.filter(c => c.id == line.masterUnitMeasurableUid)[0];
    const concept = findLineConcept(line, concepts);

    return {
        type: 'line',
        id: line && line.id != null ? Number(line.id) : 0,
        certifiedQuantity: line && line.certifiedQuantity != null ? Number(line.certifiedQuantity) : 0,
        conceptUid: concept,

        quantity: line ? (Number(line.quantity) || 0) : 0,
        price: line ? (Number(line.unityAmount) || 0) : 0,
        discount: line ? (Number(line.discount) || 0) : 0,

        isMeasurable: !!(line && line.isMeasurable === true),
        masterUnitMeasurableUid: masterUnitsMeasurable ? masterUnitsMeasurable : null,
        quantityMeasurable: line && line.quantityMeasurable != null ? Number(line.quantityMeasurable) : 1,

        tax: mapApiTaxesToUiSelection(line && line.taxes ? line.taxes : [], taxesLookup)
    };
}

function mapApiTaxesToUiSelection(apiTaxes: any[], taxesLookup: any[]): any[] {
    const res: any[] = [];
    const list = apiTaxes || [];

    for (let i = 0; i < list.length; i++) {
        const t = list[i];
        if (!t) continue;

        let found = null;

        // por uid (el campo uid del DTO guardado = MasterModel.id)
        if (t.uid) {
            for (let j = 0; j < taxesLookup.length; j++) {
                if (taxesLookup[j] && taxesLookup[j].id === t.uid) { found = taxesLookup[j]; break; }
            }
        }

        // fallback por metadata.id == taxId
        if (!found && t.taxId != null) {
            for (let k = 0; k < taxesLookup.length; k++) {
                const x = taxesLookup[k];
                if (x && x.metadata && x.metadata.id === t.taxId) { found = x; break; }
            }
        }

        // fallback por metadata.id == id (cuando el backend devuelve id en lugar de taxId)
        if (!found && t.id != null) {
            for (let k = 0; k < taxesLookup.length; k++) {
                const x = taxesLookup[k];
                if (x && x.metadata && x.metadata.id === t.id) { found = x; break; }
            }
        }

        // fallback por alias/display (registros muy antiguos sin uid ni taxId válidos)
        if (!found && t.alias) {
            for (let k = 0; k < taxesLookup.length; k++) {
                const x = taxesLookup[k];
                if (x && x.display === t.alias) { found = x; break; }
            }
        }

        // último recurso: coincidencia por type + taxImport
        if (!found && t.type && t.taxImport != null) {
            for (let k = 0; k < taxesLookup.length; k++) {
                const x = taxesLookup[k];
                if (x && x.metadata && x.metadata.type === t.type && Number(x.metadata.import) === Number(t.taxImport)) {
                    found = x; break;
                }
            }
        }

        if (found) res.push(found);
    }

    return res;
}

function findPayMethodByDisplay(payMethods: any[], display: string): any {
    if (!display) return null;
    for (let i = 0; i < (payMethods || []).length; i++) {
        const p = payMethods[i];
        if (p && p.display === display) return p;
    }
    return null;
}

function findConceptByIdOrUid(concepts: any[], id: any): any {
    if (!id) return null;
    for (let i = 0; i < (concepts || []).length; i++) {
        const c = concepts[i];
        if (c && (c.id === id || c.uid === id)) return c;
    }
    // fallback: devuelve el id tal cual si tu autocomplete acepta string
    return id;
}

function findContactByIdOrUid(contacts: any[], id: any): any {
    if (!id) return null;
    for (let i = 0; i < (contacts || []).length; i++) {
        const c = contacts[i];
        if (c && (c.id === id || c.uid === id)) return c;
    }
    // fallback: devuelve el id tal cual si tu autocomplete acepta string
    return id;
}

function calcFlags(items: any[]): any {
    let anyMeasurable = false;
    let anyDiscount = false;

    for (let i = 0; i < (items || []).length; i++) {
        const it = items[i];
        if (!it || it.type !== 'line') continue;

        if (it.isMeasurable === true || it.masterUnitMeasurableUid) anyMeasurable = true;
        if (Number(it.discount) > 0) anyDiscount = true;
    }

    return { isMeasurable: anyMeasurable, applyDiscount: anyDiscount };
}

function findLineConcept(line: any, concepts: any[]): any {
    const uid = line && line.conceptUid != null ? String(line.conceptUid).toLowerCase() : '';
    const found = uid ? (concepts || []).filter(c => c && c.id != null && String(c.id).toLowerCase() === uid)[0] : null;
    const product = line && line.product != null ? String(line.product).trim() : '';
    // Conservar el texto guardado (p. ej. "Nombre - ISBN: …"); si no, al reabrir y guardar se perdería.
    if (found) return product && product !== found.display ? { ...found, display: product } : found;
    return product ? { id: product, display: product } : null;
}
